<template>
  <div class="approval-detail-page">
    <basic-container>
      <!-- 顶部状态条 -->
      <div class="detail-banner">
        <div class="banner-left">
          <el-button class="back-btn" icon="el-icon-back" @click="handleBack">返回</el-button>
          <div class="banner-divider"></div>
          <div class="banner-info">
            <div class="banner-title-row">
              <span class="banner-title">{{ instance.title || "审批详情" }}</span>
              <span class="status-badge" :class="'status-' + instance.status">
                <i :class="statusIcon(instance.status)"></i>
                {{ statusLabel(instance.status) }}
              </span>
            </div>
            <div class="banner-meta">
              <span class="meta-item">
                <span class="avatar">{{ avatarText(instance.applicantName) }}</span>
                <span class="meta-text">{{ instance.applicantName || "-" }} 发起</span>
              </span>
              <span class="meta-sep"></span>
              <span class="meta-item"><i class="el-icon-time"></i>{{ displayTime(instance.submitTime) }}</span>
              <span class="meta-sep"></span>
              <span class="meta-item"><i class="el-icon-refresh"></i>第 {{ instance.round }} 轮</span>
              <template v-if="instance.status === 'PENDING'">
                <span class="meta-sep"></span>
                <span class="meta-item"><i class="el-icon-s-flag"></i>当前节点 {{ instance.currentNodeOrder }}/{{ instance.nodeCount }}</span>
              </template>
            </div>
          </div>
        </div>
        <div class="banner-right">
          <template v-if="instance.status === 'PENDING'">
            <el-button
              v-if="isCurrentApprover"
              type="primary"
              icon="el-icon-check"
              class="action-btn action-approve"
              @click="openAuditDialog('APPROVE')"
            >
              通过
            </el-button>
            <el-button
              v-if="isCurrentApprover"
              type="danger"
              icon="el-icon-close"
              class="action-btn action-reject"
              @click="openAuditDialog('REJECT')"
            >
              驳回
            </el-button>
            <el-button
              v-if="isApplicant"
              icon="el-icon-remove-outline"
              class="action-btn action-cancel"
              @click="openCancelDialog"
            >
              撤销
            </el-button>
          </template>
          <el-button
            v-if="instance.status === 'REJECTED' && isApplicant"
            type="primary"
            icon="el-icon-refresh-right"
            class="action-btn action-resubmit"
            @click="openResubmitDialog"
          >
            重新编辑提交
          </el-button>
        </div>
      </div>

      <el-row :gutter="16" v-loading="loading">
        <!-- 左列：基本信息 + 审批进度 -->
        <el-col :xs="24" :sm="24" :md="10">
          <!-- 基本信息 -->
          <el-card class="info-card" shadow="never">
            <div slot="header" class="card-header">
              <span class="card-title"><i class="card-title-bar"></i>基本信息</span>
            </div>
            <div class="info-grid">
              <div class="info-item info-item-full">
                <div class="info-label">审批标题</div>
                <div class="info-value">{{ instance.title || "-" }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">发起人</div>
                <div class="info-value">{{ instance.applicantName || "-" }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">提交时间</div>
                <div class="info-value">{{ displayTime(instance.submitTime) || "-" }}</div>
              </div>
              <div class="info-item">
                <div class="info-label">审批轮次</div>
                <div class="info-value">第 {{ instance.round }} 轮</div>
              </div>
              <div class="info-item">
                <div class="info-label">当前节点</div>
                <div class="info-value">
                  <template v-if="instance.status === 'PENDING'">{{ instance.currentNodeOrder }}/{{ instance.nodeCount }}</template>
                  <template v-else>-</template>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">完成时间</div>
                <div class="info-value">{{ displayTime(instance.finishTime) || "-" }}</div>
              </div>
              <div v-if="instance.finishReason" class="info-item info-item-full">
                <div class="info-label">结束原因</div>
                <div class="info-value">{{ instance.finishReason }}</div>
              </div>
            </div>
          </el-card>

          <!-- 审批进度 -->
          <el-card class="node-card" shadow="never">
            <div slot="header" class="card-header">
              <span class="card-title"><i class="card-title-bar"></i>审批进度</span>
            </div>
            <div v-for="(roundNodes, round) in nodesByRound" :key="round" class="round-group">
              <div class="round-title">第 {{ round }} 轮</div>
              <div
                v-for="node in roundNodes"
                :key="node.nodeOrder"
                class="flow-node"
                :class="nodeStatusClass(node.status)"
              >
                <div class="flow-dot">
                  <i :class="nodeStatusIcon(node.status)"></i>
                </div>
                <div class="flow-content">
                  <div class="flow-head">
                    <span class="node-name">{{ node.approverName || "-" }}</span>
                    <span class="node-status" :class="nodeStatusClass(node.status)">
                      {{ nodeStatusLabel(node.status) }}
                    </span>
                  </div>
                  <div v-if="node.auditTime" class="flow-time">
                    <i class="el-icon-time"></i>{{ displayTime(node.auditTime) }}
                  </div>
                  <div v-if="node.opinion" class="flow-opinion">
                    <span class="opinion-label">意见</span>{{ node.opinion }}
                  </div>
                </div>
              </div>
            </div>
            <el-empty v-if="!nodes.length" description="暂无审批记录" :image-size="70" />
          </el-card>
        </el-col>

        <!-- 右列：明细 -->
        <el-col :xs="24" :sm="24" :md="14">
          <el-card class="biz-card" shadow="never">
            <div slot="header" class="card-header card-header-between">
              <span class="card-title"><i class="card-title-bar"></i>明细</span>
              <span v-if="bizDetail.items && bizDetail.items.length" class="biz-count">
                共 {{ bizDetail.items.length }} 条
              </span>
            </div>
            <div class="biz-summary">
              <div class="biz-summary-item">
                <span class="biz-summary-label">价目表</span>
                <span class="biz-summary-value">{{ bizDetail.priceBookName || "-" }}</span>
              </div>
              <div class="biz-summary-item">
                <span class="biz-summary-label">项目类型</span>
                <span class="biz-summary-value project-type">{{ projectTypeLabel(bizDetail.projectType) }}</span>
              </div>
              <template v-if="isCancelAdjust">
                <div class="biz-summary-item">
                  <span class="biz-summary-label">申请类型</span>
                  <span class="biz-summary-value" style="color: #f56c6c">作废申请</span>
                </div>
              </template>
              <template v-else-if="isNewAdjustType">
                <div class="biz-summary-item">
                  <span class="biz-summary-label">调价方式</span>
                  <span class="biz-summary-value">
                    {{ bizDetail.adjustTypeName || adjustTypeLabel(bizDetail.adjustType) }}
                    <template v-if="bizDetail.adjustType === 'FORMULA'">：{{ bizDetail.formula }}</template>
                    <template v-else-if="bizDetail.adjustValue != null">：{{ adjustParamDisplay(bizDetail) }}</template>
                  </span>
                </div>
                <div class="biz-summary-item biz-summary-item-full">
                  <span class="biz-summary-label">有效期</span>
                  <span class="biz-summary-value">
                    {{ displayTime(bizDetail.effectiveStartTime) || "-" }}
                    <span style="margin: 0 6px; color: #c9cdd4">~</span>
                    {{ displayTime(bizDetail.effectiveEndTime) || "-" }}
                  </span>
                </div>
              </template>
            </div>

            <!-- 新类型（PRICE_BOOK_ADJUST_TASK）：按 adjustType + originalPrice/adjustedPrice 渲染 -->
            <el-table
              v-if="isNewAdjustType"
              :data="bizDetail.items || []"
              class="biz-table"
              size="medium"
            >
              <el-table-column type="index" label="序号" width="60" align="center" />
              <el-table-column prop="merchantName" label="商户" min-width="130" show-overflow-tooltip />
              <el-table-column prop="merchantPhone" label="联系电话" min-width="120">
                <template slot-scope="scope">{{ scope.row.merchantPhone || "-" }}</template>
              </el-table-column>
              <el-table-column prop="goodsFilterName" label="型号" min-width="120" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.goodsFilterName || "-" }}</template>
              </el-table-column>
              <el-table-column label="当前价(元/㎡)" prop="originalPrice" width="110" align="center">
                <template slot-scope="scope">{{ scope.row.originalPrice }}</template>
              </el-table-column>
              <el-table-column label="上次调价(元/㎡)" prop="latestAdjustedPrice" width="130" align="center">
                <template slot-scope="scope">
                  <span v-if="scope.row.latestAdjustedPrice == null" class="empty-text">-</span>
                  <span v-else style="color: #909399">{{ scope.row.latestAdjustedPrice}}</span>
                </template>
              </el-table-column>
              <el-table-column label="本次新价(元/㎡)" prop="adjustedPrice" width="120" align="center">
                <template slot-scope="scope">
                  <span style="color: #f56c6c; font-weight: 600">{{ scope.row.adjustedPrice }}</span>
                </template>
              </el-table-column>
              <el-table-column label="价差(元/㎡)" width="110" align="center">
                <template slot-scope="scope">
                  <span :style="{ color: priceDiffColor(scope.row), fontWeight: 600 }">
                    {{ priceDiffText(scope.row) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="调价说明" min-width="160">
                <template slot-scope="scope">
                  <span class="adjust-item-desc">{{ adjustItemDesc(scope.row) }}</span>
                </template>
              </el-table-column>
            </el-table>

            <!-- 作废申请（bizDetail.action = CANCEL）：originalPrice → adjustedPrice，行级有效期 -->
            <el-table
              v-else-if="isCancelAdjust"
              :data="bizDetail.items || []"
              class="biz-table"
              size="medium"
            >
              <el-table-column type="index" label="序号" width="60" align="center" />
              <el-table-column prop="merchantName" label="商户" min-width="130" show-overflow-tooltip />
              <el-table-column prop="merchantPhone" label="联系电话" min-width="120">
                <template slot-scope="scope">{{ scope.row.merchantPhone || "-" }}</template>
              </el-table-column>
              <el-table-column prop="goodsFilterName" label="型号" min-width="120" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.goodsFilterName || "-" }}</template>
              </el-table-column>
              <el-table-column label="当前调价(元/㎡)" min-width="190" align="center">
                <template slot-scope="scope">
                  <span class="price-change">
                    <span class="price-old">{{ scope.row.originalPrice }}</span>
                    <i class="el-icon-right price-arrow"></i>
                    <span class="price-new">{{ scope.row.adjustedPrice }}</span>
                    <span class="price-unit">元/㎡</span>
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="有效期" min-width="240">
                <template slot-scope="scope">
                  {{ displayTime(scope.row.effectiveStartTime) || "-" }}
                  <span style="margin: 0 6px; color: #c9cdd4">~</span>
                  {{ displayTime(scope.row.effectiveEndTime) || "-" }}
                </template>
              </el-table-column>
            </el-table>

            <!-- 旧类型（PRICE_BOOK_MERCHANT_ADJUST）：action/oldPrice/price -->
            <el-table
              v-else
              :data="bizDetail.items || []"
              class="biz-table"
              size="medium"
            >
              <el-table-column type="index" label="序号" width="60" align="center" />
              <el-table-column label="操作类型" width="100" align="center">
                <template slot-scope="scope">
                  <span class="action-tag" :class="'action-' + scope.row.action">
                    {{ actionLabel(scope.row.action) }}
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="merchantName" label="商户" min-width="130" show-overflow-tooltip />
              <el-table-column prop="merchantPhone" label="联系电话" min-width="120">
                <template slot-scope="scope">{{ scope.row.merchantPhone || "-" }}</template>
              </el-table-column>
              <el-table-column prop="goodsFilterName" label="型号" min-width="120" show-overflow-tooltip>
                <template slot-scope="scope">{{ scope.row.goodsFilterName || "-" }}</template>
              </el-table-column>
              <el-table-column label="价格变化" min-width="180" align="center">
                <template slot-scope="scope">
                  <span v-if="scope.row.action === 'ADD'" class="price-change">
                    <span class="price-new">+{{ scope.row.price }}</span>
                    <span class="price-unit">元/㎡</span>
                  </span>
                  <span v-else-if="scope.row.action === 'UPDATE'" class="price-change">
                    <span class="price-old">{{ scope.row.oldPrice }}</span>
                    <i class="el-icon-right price-arrow"></i>
                    <span v-if="scope.row.latestAdjustedPrice == null" class="empty-text">-</span>
                    <span v-else class="price-mid">{{ scope.row.latestAdjustedPrice}}</span>
                    <i class="el-icon-right price-arrow"></i>
                    <span class="price-new">{{ scope.row.price }}</span>
                    <span class="price-unit">元/㎡</span>
                  </span>
                  <span v-else class="price-change">
                    <span class="price-old">{{ scope.row.oldPrice }}</span>
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="说明" min-width="110">
                <template slot-scope="scope">
                  <span class="change-desc" :class="'action-' + scope.row.action">
                    <template v-if="scope.row.action === 'ADD'">新增价格</template>
                    <template v-else-if="scope.row.action === 'UPDATE'">修改价格</template>
                    <template v-else>删除价格</template>
                  </span>
                </template>
              </el-table-column>
            </el-table>

            <!-- 提示语（调价申请/作废申请） -->
            <el-alert
              v-if="(isNewAdjustType || isCancelAdjust) && bizDetail.notice"
              :title="bizDetail.notice"
              type="info"
              :closable="false"
              show-icon
              class="biz-notice"
            />
            <el-empty
              v-if="!(bizDetail.items || []).length"
              description="无明细"
              :image-size="80"
            />
          </el-card>
        </el-col>
      </el-row>
    </basic-container>

    <!-- 通过/驳回弹窗 -->
    <el-dialog
      :title="auditDialog.title"
      :visible.sync="auditDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      custom-class="approval-dialog"
    >
      <el-form ref="auditForm" :model="auditForm" :rules="auditRules" label-width="90px">
        <el-form-item :label="auditDialog.action === 'REJECT' ? '驳回原因' : '审批意见'" prop="opinion">
          <el-input
            v-model="auditForm.opinion"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            :placeholder="auditDialog.action === 'REJECT' ? '请输入驳回原因（必填，将展示给提交人）' : '请输入审批意见（可填）'"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="auditDialogVisible = false">取 消</el-button>
        <el-button
          :type="auditDialog.action === 'REJECT' ? 'danger' : 'primary'"
          :loading="submitting"
          @click="handleAuditSubmit"
        >
          确 定
        </el-button>
      </div>
    </el-dialog>

    <!-- 撤销弹窗 -->
    <el-dialog
      title="撤销审批"
      :visible.sync="cancelDialogVisible"
      width="480px"
      :close-on-click-modal="false"
      custom-class="approval-dialog"
    >
      <el-form ref="cancelForm" :model="cancelForm" :rules="cancelRules" label-width="90px">
        <el-form-item label="撤销原因" prop="reason">
          <el-input
            v-model="cancelForm.reason"
            type="textarea"
            :rows="3"
            maxlength="200"
            show-word-limit
            placeholder="请输入撤销原因"
          />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="cancelDialogVisible = false">取 消</el-button>
        <el-button type="warning" :loading="submitting" @click="handleCancelSubmit">确 定</el-button>
      </div>
    </el-dialog>

    <!-- 驳回后重新编辑提交弹窗 -->
    <el-dialog
      title="重新编辑并提交"
      :visible.sync="resubmitDialogVisible"
      width="860px"
      :close-on-click-modal="false"
      custom-class="approval-dialog"
      @closed="resetResubmitForm"
    >
      <el-alert
        title="修改后重新提交，将按最新审批流程重新走审批，历史审批记录留痕可查。"
        type="warning"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
      />
      <el-table :data="resubmitItems" border size="small" max-height="420">
        <el-table-column label="操作类型" width="90" align="center">
          <template slot-scope="scope">
            <el-tag :type="actionTagType(scope.row.action)" size="mini">
              {{ isCancelAdjust ? "作废" : actionLabel(scope.row.action) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="merchantName" label="商户" min-width="120" show-overflow-tooltip />
        <el-table-column prop="goodsFilterName" label="型号" min-width="110" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ scope.row.goodsFilterName || "-" }}
          </template>
        </el-table-column>
        <el-table-column label="价格(元/㎡)" width="140" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-if="resubmitEditEnabled && scope.row.action !== 'DELETE'"
              v-model="scope.row.price"
              :min="0"
              :precision="2"
              size="small"
              controls-position="right"
            />
            <span v-else-if="resubmitEditEnabled" style="color: #909399">删除</span>
            <span v-else style="color: #c0c4cc">-</span>
          </template>
        </el-table-column>
        <el-table-column label="原价(元/㎡)" width="110" align="center">
          <template slot-scope="scope">
            {{ scope.row.oldPrice == null ? "-" : scope.row.oldPrice }}
          </template>
        </el-table-column>
        <el-table-column label="有效期" min-width="210">
          <template slot-scope="scope">
            <template v-if="isCancelAdjust">
              {{ displayTime(scope.row.effectiveStartTime) || "-" }}
              <span style="margin: 0 6px; color: #c9cdd4">~</span>
              {{ displayTime(scope.row.effectiveEndTime) || "-" }}
            </template>
            <span v-else>-</span>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button @click="resubmitDialogVisible = false">取 消</el-button>
        <el-button type="primary" :loading="submitting" @click="handleResubmitSubmit">提交审批</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { formatterTime } from "@/util/util";
import {
  approvalStatusDic,
  approvalNodeStatusDic,
  approvalActionDic,
} from "@/const/work/waterProof/approvalOption";
import {
  getApprovalDetail,
  auditApproval,
  cancelApproval,
  resubmitApproval,
} from "@/api/work/waterProof/approval";
import Empty from "element-ui/lib/empty";

export default {
  name: "approvalDetail",
  components: {
    "el-empty": Empty,
  },
  data() {
    return {
      loading: false,
      submitting: false,
      instance: {},
      nodes: [],
      bizDetail: { items: [] },
      userInfo: {},
      auditDialogVisible: false,
      auditDialog: { action: "APPROVE", title: "" },
      auditForm: { instanceId: "", action: "", opinion: "" },
      auditRules: {
        opinion: [{ required: false, message: "", trigger: "blur" }],
      },
      cancelDialogVisible: false,
      cancelForm: { instanceId: "", reason: "" },
      cancelRules: {
        reason: [{ required: true, message: "请输入撤销原因", trigger: "blur" }],
      },
      resubmitDialogVisible: false,
      resubmitItems: [],
      resubmitEditEnabled: true,
    };
  },
  computed: {
    instanceId() {
      return this.$route.params.id;
    },
    // 按轮次分组展示节点进度
    nodesByRound() {
      const map = {};
      (this.nodes || []).forEach((n) => {
        const r = n.round || 1;
        if (!map[r]) map[r] = [];
        map[r].push(n);
      });
      return map;
    },
    // 当前 PENDING 节点（当前轮次正在审批的节点）
    currentNode() {
      const pending = (this.nodes || []).find(
        (n) => n.status === "PENDING"
      );
      return pending || null;
    },
    // 当前登录人是否是当前节点审批人
    isCurrentApprover() {
      const myName = this.currentUserName;
      return (
        this.instance.status === "PENDING" &&
        !!this.currentNode &&
        !!myName &&
        this.currentNode.approverName === myName
      );
    },
    // 当前登录人是否是提交人
    isApplicant() {
      const myName = this.currentUserName;
      return !!myName && this.instance.applicantName === myName;
    },
    currentUserName() {
      const u = this.userInfo || {};
      return u.name || u.realName || u.username || "";
    },
    // 新审批类型（PRICE_BOOK_ADJUST_TASK）：bizDetail 含 adjustType + items[].originalPrice/adjustedPrice
    // 作废申请（PRICE_BOOK_ADJUST_TASK，bizDetail.action = CANCEL）：无 adjustType，行级有效期
    // 旧审批类型（PRICE_BOOK_MERCHANT_ADJUST）：bizDetail.items[].action/oldPrice/price
    isNewAdjustType() {
      return !!(this.bizDetail && this.bizDetail.adjustType);
    },
    isCancelAdjust() {
      return !!(this.bizDetail && this.bizDetail.action === "CANCEL");
    },
  },
  created() {
    this.userInfo = this.$store.getters.userInfo || {};
  },
  mounted() {
    this.handleDetail();
  },
  methods: {
    formatTime: formatterTime,
    // 表格展示：后端返回展示格式字符串时直接显示，时间戳则走 formatterTime
    displayTime(v) {
      if (v == null || v === "") return "";
      if (typeof v === "string" && !/^\d+$/.test(v)) return v;
      return this.formatTime(v);
    },
    parseData(res) {
      if (!res || res.data == null) return {};
      if (typeof res.data === "string") {
        try {
          return JSON.parse(res.data) || {};
        } catch (e) {
          console.log("parseData error:", e);
          return {};
        }
      }
      return res.data;
    },
    // 兼容：applyData 可能是 JSON 字符串或已解析对象
    parseBizDetail(data) {
      if (!data) return { items: [] };
      if (typeof data === "string") {
        try {
          return JSON.parse(data) || { items: [] };
        } catch (e) {
          return { items: [] };
        }
      }
      return data;
    },
    statusLabel(val) {
      const item = approvalStatusDic.find((d) => d.value === val);
      return item ? item.label : val || "-";
    },
    statusTagType(val) {
      const map = {
        PENDING: "warning",
        APPROVED: "success",
        REJECTED: "danger",
        CANCELLED: "info",
        APPLY_FAILED: "danger",
      };
      return map[val] || "info";
    },
    statusIcon(val) {
      const map = {
        PENDING: "el-icon-time",
        APPROVED: "el-icon-circle-check",
        REJECTED: "el-icon-circle-close",
        CANCELLED: "el-icon-circle-close",
        APPLY_FAILED: "el-icon-warning-outline",
      };
      return map[val] || "el-icon-info";
    },
    avatarText(name) {
      if (!name) return "?";
      return name.substring(0, 1);
    },
    nodeStatusLabel(val) {
      const item = approvalNodeStatusDic.find((d) => d.value === val);
      return item ? item.label : val || "-";
    },
    nodeTagType(val) {
      const map = {
        WAITING: "info",
        PENDING: "warning",
        APPROVED: "success",
        REJECTED: "danger",
        SKIPPED: "info",
      };
      return map[val] || "info";
    },
    nodeStatusIcon(val) {
      const map = {
        WAITING: "el-icon-more",
        PENDING: "el-icon-time",
        APPROVED: "el-icon-check",
        REJECTED: "el-icon-close",
        SKIPPED: "el-icon-minus",
      };
      return map[val] || "el-icon-more";
    },
    nodeStatusClass(val) {
      return "status-" + (val || "").toLowerCase();
    },
    actionLabel(val) {
      const item = approvalActionDic.find((d) => d.value === val);
      return item ? item.label : val || "-";
    },
    actionTagType(val) {
      const map = { ADD: "danger", UPDATE: "warning", DELETE: "info" };
      return map[val] || "info";
    },
    projectTypeLabel(val) {
      const map = { WATERPROOF: "防水", BACKFILL: "回填", HANG_CEILING: "吊顶" };
      return map[val] || val || "-";
    },
    // 调整方式 code → 中文（兜底映射，对应 PRICE_BOOK_ADJUST_TASK 的 4 种 adjustType）
    adjustTypeLabel(val) {
      const map = {
        FIXED: "固定价",
        DISCOUNT: "折扣率",
        ADD: "加减金额",
        FORMULA: "公式价",
      };
      return map[val] || val || "-";
    },
    // 头部"调价方式"后显示的参数：FIXED=金额；DISCOUNT=折扣率；ADD=带正负号的金额；FORMULA 不在这里显示
    adjustParamDisplay(bizDetail) {
      const v = bizDetail.adjustValue;
      if (v == null) return "-";
      const t = bizDetail.adjustType;
      if (t === "DISCOUNT") return v; // 0.93
      if (t === "ADD") return (v > 0 ? "+" : "") + v; // -2 / +0.5
      return v; // FIXED 金额
    },
    // 单条调价说明：基于整单的 adjustType + adjustValue/formula
    adjustItemDesc(_item) {
      const t = this.bizDetail.adjustType;
      const v = this.bizDetail.adjustValue;
      if (t === "FIXED") return `固定价 ${v} 元/㎡`;
      if (t === "DISCOUNT") return `按 ${v} 折扣`;
      if (t === "ADD") return `${v > 0 ? "+" : ""}${v} 元/㎡`;
      if (t === "FORMULA") return `公式：${this.bizDetail.formula || ""}`;
      return "-";
    },
    // 价差文本：原价 → 调整后
    priceDiffText(item) {
      const o = Number(item.originalPrice);
      const a = Number(item.adjustedPrice);
      if (isNaN(o) || isNaN(a)) return "-";
      const diff = +(a - o).toFixed(2);
      if (diff === 0) return "0";
      return (diff > 0 ? "+" : "") + diff.toFixed(2);
    },
    priceDiffColor(item) {
      const o = Number(item.originalPrice);
      const a = Number(item.adjustedPrice);
      if (isNaN(o) || isNaN(a)) return "#909399";
      const diff = +(a - o).toFixed(2);
      if (diff > 0) return "#f56c6c";   // 涨价红
      if (diff < 0) return "#67c23a";   // 降价绿
      return "#909399";
    },
    handleDetail() {
      this.loading = true;
      getApprovalDetail(this.instanceId)
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            // 兼容两种返回结构：
            // 1) { instance, nodes, bizDetail } 分字段结构
            // 2) 实例对象本身（含 applyData / nodes）
            let instance = data.instance || data;
            if (Array.isArray(instance)) instance = instance[0] || {};
            // bizDetail 优先级：data.bizDetail > instance.applyData
            let bizDetail =
              data.bizDetail != null
                ? this.parseBizDetail(data.bizDetail)
                : this.parseBizDetail(instance.applyData);
            this.instance = instance || {};
            this.nodes =
              (Array.isArray(data.nodes)
                ? data.nodes
                : Array.isArray(instance.nodes)
                  ? instance.nodes
                  : []) || [];
            this.bizDetail = bizDetail || { items: [] };
          } else {
            this.$message.error(res.message || "加载失败");
          }
        })
        .catch((err) => {
          console.log("handleDetail error:", err);
          this.$message.error("加载异常");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleBack() {
      // 关闭当前详情标签，再跳到上一个标签，避免「返回」后顶部还挂着「审批详情」
      const tagsState = (this.$store && this.$store.state && this.$store.state.tags) || {};
      const tagList = tagsState.tagList || [];
      const currentTag = tagsState.tag;
      const currentIdx = currentTag
        ? tagList.findIndex((t) => t && t.value === currentTag.value)
        : -1;
      if (currentIdx >= 0) {
        this.$store.commit("DEL_TAG", currentTag);
        const newList = this.$store.state.tags.tagList || [];
        if (newList.length === 0) {
          // 深链直达场景：原本只有这一个标签，关闭后跳回审批中心列表
          this.$router.push("/work/waterProof/merchantPrice/approval");
        } else {
          // 跳到上一个标签（与 tags.vue 的 closeTag 自动跳转逻辑一致）
          const prevIdx = currentIdx === 0 ? 0 : currentIdx - 1;
          const prev = newList[prevIdx];
          this.$router.push({ path: prev.value, query: prev.query });
        }
        return;
      }
      // 兜底：找不到当前标签时按原逻辑返回
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push("/work/waterProof/merchantPrice/approval");
      }
    },
    // 通过 / 驳回
    openAuditDialog(action) {
      this.auditDialog = {
        action,
        title: action === "APPROVE" ? "通过审批" : "驳回审批",
      };
      this.auditForm = {
        instanceId: this.instance.id,
        action,
        opinion: "",
      };
      if (action === "REJECT") {
        this.auditRules.opinion[0].required = true;
      } else {
        this.auditRules.opinion[0].required = false;
      }
      this.auditDialogVisible = true;
    },
    handleAuditSubmit() {
      this.$refs.auditForm.validate((valid) => {
        if (!valid) return;
        this.submitting = true;
        auditApproval(this.auditForm)
          .then((res) => {
            if (res.code === "200") {
              this.$message.success(this.auditDialog.action === "APPROVE" ? "已通过" : "已驳回");
              this.auditDialogVisible = false;
              this.handleDetail();
            } else {
              this.$message.error(res.message || "操作失败");
            }
          })
          .catch((err) => {
            console.log("auditApproval error:", err);
            this.$message.error("操作异常");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    // 撤销
    openCancelDialog() {
      this.cancelForm = { instanceId: this.instance.id, reason: "" };
      this.cancelDialogVisible = true;
    },
    handleCancelSubmit() {
      this.$refs.cancelForm.validate((valid) => {
        if (!valid) return;
        this.submitting = true;
        cancelApproval(this.cancelForm)
          .then((res) => {
            if (res.code === "200") {
              this.$message.success("已撤销");
              this.cancelDialogVisible = false;
              this.handleDetail();
            } else {
              this.$message.error(res.message || "撤销失败");
            }
          })
          .catch((err) => {
            console.log("cancelApproval error:", err);
            this.$message.error("撤销异常");
          })
          .finally(() => {
            this.submitting = false;
          });
      });
    },
    // 驳回后重新编辑提交：用 bizDetail 预填，修改价格后把明细 JSON 作为 applyData 提交
    openResubmitDialog() {
      this.resubmitItems = JSON.parse(JSON.stringify(this.bizDetail.items || []));
      // 作废申请无可编辑价格，只读展示
      this.resubmitEditEnabled = !this.isCancelAdjust;
      this.resubmitDialogVisible = true;
    },
    resetResubmitForm() {
      this.resubmitItems = [];
    },
    handleResubmitSubmit() {
      if (!this.resubmitItems.length) {
        this.$message.warning("没有可提交的明细");
        return;
      }
      this.submitting = true;
      // 作废申请需保留 action=CANCEL，否则会按调价申请处理
      const payload = {
        priceBookId: this.bizDetail.priceBookId,
        priceBookName: this.bizDetail.priceBookName,
        projectType: this.bizDetail.projectType,
        items: this.resubmitItems,
      };
      if (this.isCancelAdjust) {
        payload.action = "CANCEL";
      }
      const applyData = JSON.stringify(payload);
      resubmitApproval({ instanceId: this.instance.id, applyData })
        .then((res) => {
          if (res.code === "200") {
            this.$message.success("已重新提交审批");
            this.resubmitDialogVisible = false;
            this.handleDetail();
          } else {
            this.$message.error(res.message || "提交失败");
          }
        })
        .catch((err) => {
          console.log("resubmitApproval error:", err);
          this.$message.error("提交异常");
        })
        .finally(() => {
          this.submitting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.approval-detail-page {
  /* ============ 顶部状态条 ============ */
  .detail-banner {
    position: relative;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    padding: 20px 24px;
    margin-bottom: 16px;
    border-radius: 10px;
    border: 1px solid #e6effb;
    background: linear-gradient(135deg, #f6faff 0%, #ffffff 60%, #f9fbff 100%);
    box-shadow: 0 1px 4px rgba(64, 158, 255, 0.06);

    .banner-left {
      display: flex;
      align-items: center;
      gap: 16px;
      min-width: 0;
    }

    .back-btn {
      flex-shrink: 0;
      border-radius: 8px;
      font-weight: 500;
      transition: all 0.2s;
      &:hover {
        transform: translateX(-2px);
        border-color: #409eff;
        color: #409eff;
      }
    }

    .banner-divider {
      width: 1px;
      height: 36px;
      background: #e4e7ed;
      flex-shrink: 0;
    }

    .banner-info {
      min-width: 0;
    }

    .banner-title-row {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .banner-title {
      font-size: 18px;
      font-weight: 600;
      color: #1d2129;
      line-height: 1.4;
      letter-spacing: 0.2px;
      word-break: break-all;
    }

    .banner-meta {
      display: flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      margin-top: 8px;
    }

    .meta-item {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      font-size: 13px;
      color: #86909c;

      .avatar {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 22px;
        height: 22px;
        border-radius: 6px;
        background: linear-gradient(135deg, #409eff, #66b1ff);
        color: #fff;
        font-size: 12px;
        font-weight: 600;
      }

      .meta-text {
        color: #4e5969;
      }

      i {
        font-size: 14px;
        color: #a9b0b8;
      }
    }

    .meta-sep {
      width: 1px;
      height: 12px;
      background: #e5e6eb;
    }

    .banner-right {
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .action-btn {
      border-radius: 8px;
      font-weight: 500;
      transition: all 0.2s;

      &:active {
        transform: scale(0.96);
      }
    }

    .action-approve {
      background: linear-gradient(135deg, #409eff, #66b1ff);
      border-color: transparent;
      box-shadow: 0 4px 10px rgba(64, 158, 255, 0.28);

      &:hover {
        opacity: 0.92;
        transform: translateY(-1px);
        box-shadow: 0 6px 14px rgba(64, 158, 255, 0.35);
      }
    }

    .action-resubmit {
      background: linear-gradient(135deg, #409eff, #66b1ff);
      border-color: transparent;
      box-shadow: 0 4px 10px rgba(64, 158, 255, 0.28);

      &:hover {
        opacity: 0.92;
        transform: translateY(-1px);
        box-shadow: 0 6px 14px rgba(64, 158, 255, 0.35);
      }
    }

    .action-reject {
      box-shadow: 0 4px 10px rgba(245, 108, 108, 0.22);

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 14px rgba(245, 108, 108, 0.3);
      }
    }

    .action-cancel {
      color: #4e5969;
      &:hover {
        border-color: #e6a23c;
        color: #e6a23c;
      }
    }
  }

  /* 状态徽章 */
  .status-badge {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 12px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;

    &.status-pending {
      color: #d46b08;
      background: rgba(230, 162, 60, 0.12);
      border: 1px solid rgba(230, 162, 60, 0.3);
    }
    &.status-approved {
      color: #389e0d;
      background: rgba(103, 194, 58, 0.12);
      border: 1px solid rgba(103, 194, 58, 0.3);
    }
    &.status-rejected {
      color: #cf1322;
      background: rgba(245, 108, 108, 0.12);
      border: 1px solid rgba(245, 108, 108, 0.3);
    }
    &.status-cancelled {
      color: #4e5969;
      background: rgba(144, 147, 153, 0.12);
      border: 1px solid rgba(144, 147, 153, 0.3);
    }
    &.status-appl_failed {
      color: #cf1322;
      background: rgba(245, 108, 108, 0.12);
      border: 1px solid rgba(245, 108, 108, 0.3);
    }
  }

  /* ============ 卡片通用 ============ */
  .el-card {
    border-radius: 10px;
    border: 1px solid #f0f2f5;
    box-shadow: 0 1px 4px rgba(29, 33, 41, 0.04) !important;
    margin-bottom: 16px;
  }

  ::v-deep .el-card__header {
    padding: 14px 20px;
    border-bottom: 1px solid #f2f3f5;
    background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
    border-radius: 10px 10px 0 0;
  }

  ::v-deep .el-card__body {
    padding: 18px 20px;
  }

  .card-header {
    display: flex;
    align-items: center;

    &.card-header-between {
      justify-content: space-between;
    }
  }

  .card-title {
    display: inline-flex;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    color: #1d2129;

    .card-title-bar {
      width: 4px;
      height: 16px;
      border-radius: 2px;
      margin-right: 8px;
      background: linear-gradient(180deg, #409eff, #66b1ff);
    }
  }

  .biz-count {
    font-size: 12px;
    color: #86909c;
    background: #f2f3f5;
    padding: 2px 10px;
    border-radius: 12px;
  }

  /* ============ 基本信息 ============ */
  .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;

    .info-item-full {
      grid-column: 1 / -1;
    }

    .info-item {
      .info-label {
        font-size: 12px;
        color: #86909c;
        margin-bottom: 5px;
      }
      .info-value {
        font-size: 14px;
        color: #1d2129;
        line-height: 1.6;
        word-break: break-all;
      }
    }
  }

  /* ============ 审批进度 ============ */
  .round-group {
    margin-bottom: 18px;

    &:last-child {
      margin-bottom: 0;
    }

    .round-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      font-weight: 600;
      color: #4e5969;
      margin-bottom: 12px;

      &::before {
        content: "";
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #c9cdd4;
      }
    }

    .flow-node {
      position: relative;
      padding: 0 0 22px 36px;

      &:last-child {
        padding-bottom: 4px;
      }

      &::before {
        content: "";
        position: absolute;
        left: 11px;
        top: 30px;
        bottom: 4px;
        width: 2px;
        background: #e5e6eb;
      }
      &:last-child::before {
        display: none;
      }

      /* 通过节点的连线高亮 */
      &.status-approved::before {
        background: linear-gradient(180deg, #67c23a, #e5e6eb);
      }
      &.status-rejected::before {
        background: linear-gradient(180deg, #f56c6c, #e5e6eb);
      }

      .flow-dot {
        position: absolute;
        left: 0;
        top: 0;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-size: 12px;
        z-index: 1;
        transition: all 0.3s;
      }

      .flow-content {
        border: 1px solid #f0f2f5;
        border-radius: 8px;
        padding: 10px 14px;
        background: #fafbfc;
        transition: all 0.2s;

        &:hover {
          background: #f6f9ff;
          border-color: #d9e6f8;
        }
      }

      .flow-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;

        .node-name {
          font-size: 14px;
          font-weight: 600;
          color: #1d2129;
        }
      }

      .node-status {
        flex-shrink: 0;
        font-size: 12px;
        padding: 1px 10px;
        border-radius: 12px;

        &.status-approved {
          color: #389e0d;
          background: rgba(103, 194, 58, 0.12);
        }
        &.status-rejected {
          color: #cf1322;
          background: rgba(245, 108, 108, 0.12);
        }
        &.status-pending {
          color: #d46b08;
          background: rgba(230, 162, 60, 0.14);
        }
        &.status-waiting,
        &.status-skipped {
          color: #86909c;
          background: rgba(144, 147, 153, 0.12);
        }
      }

      .flow-time {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #86909c;
        margin-top: 6px;

        i {
          font-size: 13px;
        }
      }

      .flow-opinion {
        margin-top: 8px;
        padding: 8px 10px;
        background: #fff;
        border: 1px dashed #e5e6eb;
        border-radius: 6px;
        font-size: 12px;
        color: #4e5969;
        line-height: 1.7;

        .opinion-label {
          color: #a9b0b8;
          margin-right: 6px;
          font-size: 12px;
        }
      }
    }
  }

  /* 节点状态配色（圆点） */
  .flow-node.status-approved .flow-dot {
    background: linear-gradient(135deg, #67c23a, #85ce61);
    box-shadow: 0 2px 6px rgba(103, 194, 58, 0.35);
  }
  .flow-node.status-rejected .flow-dot {
    background: linear-gradient(135deg, #f56c6c, #f78989);
    box-shadow: 0 2px 6px rgba(245, 108, 108, 0.35);
  }
  .flow-node.status-pending .flow-dot {
    background: linear-gradient(135deg, #e6a23c, #eebb52);
    box-shadow: 0 2px 6px rgba(230, 162, 60, 0.4);
    animation: dot-pulse 2s ease-in-out infinite;
  }
  .flow-node.status-waiting .flow-dot,
  .flow-node.status-skipped .flow-dot {
    background: #c9cdd4;
  }

  /* ============ 明细 ============ */
  .biz-summary {
    display: flex;
    align-items: center;
    gap: 24px;
    flex-wrap: wrap;
    padding: 12px 16px;
    margin-bottom: 14px;
    background: #f8faff;
    border: 1px solid #e6effb;
    border-radius: 8px;

    .biz-summary-item {
      display: inline-flex;
      align-items: center;
      gap: 8px;

      .biz-summary-label {
        font-size: 12px;
        color: #86909c;
      }
      .biz-summary-value {
        font-size: 14px;
        font-weight: 600;
        color: #1d2129;
      }
      .project-type {
        color: #409eff;
      }
    }
    .biz-summary-item-full {
      flex-basis: 100%;
    }
    .biz-notice {
      margin-top: 12px;
    }
  }

  .biz-table {
    width: 100%;

    ::v-deep .el-table__header th {
      background: #fafbfc;
      color: #4e5969;
      font-weight: 600;
      font-size: 13px;
      padding: 10px 0;
    }

    ::v-deep .el-table__row {
      transition: background 0.2s;

      td {
        padding: 12px 0;
        font-size: 13px;
        color: #1d2129;
      }

      &:hover > td {
        background: #f6f9ff;
      }
    }

    .action-tag {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;

      &.action-add {
        color: #389e0d;
        background: rgba(103, 194, 58, 0.12);
      }
      &.action-update {
        color: #d46b08;
        background: rgba(230, 162, 60, 0.12);
      }
      &.action-delete {
        color: #86909c;
        background: rgba(144, 147, 153, 0.12);
      }
    }

    .price-change {
      display: inline-flex;
      align-items: center;
      gap: 6px;

      .price-old {
        color: #86909c;
        text-decoration: line-through;
        font-size: 13px;
      }
      .price-mid {
        color: #909399;
        font-size: 13px;
        font-variant-numeric: tabular-nums;
      }
      .price-new {
        color: #f53f3f;
        font-weight: 600;
        font-size: 14px;
        font-variant-numeric: tabular-nums;
      }
      .price-unit {
        color: #a9b0b8;
        font-size: 12px;
      }
      .price-arrow {
        color: #409eff;
        font-size: 14px;
      }
      .empty-text {
        color: #c0c4cc;
      }
    }

    .change-desc {
      font-size: 12px;

      &.action-add {
        color: #389e0d;
      }
      &.action-update {
        color: #409eff;
      }
      &.action-delete {
        color: #86909c;
      }
    }
    .adjust-item-desc {
      font-size: 13px;
      color: #4e5969;
    }
  }

  /* ============ 弹窗 ============ */
  ::v-deep .approval-dialog {
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
      padding: 16px 24px;
      border-bottom: 1px solid #f2f3f5;
      background: #fafbfc;

      .el-dialog__title {
        font-size: 16px;
        font-weight: 600;
        color: #1d2129;
      }
    }

    .el-dialog__body {
      padding: 20px 24px;
    }

    .el-dialog__footer {
      padding: 12px 24px;
      border-top: 1px solid #f2f3f5;
    }
  }

  @keyframes dot-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(230, 162, 60, 0.45);
    }
    70% {
      box-shadow: 0 0 0 8px rgba(230, 162, 60, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(230, 162, 60, 0);
    }
  }

  /* 窄屏适配 */
  @media (max-width: 992px) {
    .detail-banner {
      flex-direction: column;
      align-items: flex-start;
    }
    .banner-right {
      width: 100%;
      justify-content: flex-start;
    }
    .info-grid {
      grid-template-columns: 1fr;
      .info-item-full {
        grid-column: auto;
      }
    }
  }
}
</style>
